import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RxUtils, Tr } from '@utils';
import { useLoggedinUser, useService } from '@hooks';
import { Permission, CarListBand, carListBandService, notificationService } from '@services';
import { Button, Stack } from '@mui/material';
import { EntityDeleteConfirmationDialog } from '@components/common';
import { PortalPageTitle, EmptyEntityPageLayout } from '@components/portal/common';
import { CarListBandList, CarListBandAddUpdateDialog } from '@components/portal/car-list-bands';
import { ReactComponent as Illustration } from '@assets/illustrations/no-car-list-bands-added.svg';

const paddingX = 9;

export const CarListBands: FC = () => {
	const user = useLoggedinUser();
	const { t } = useTranslation('portal');
	const [searchText, setSearchText] = useState('');
	const [searchResult, setSearchResult] = useState<CarListBand[]>([]);
	const [editingEntity, setEditingEntity] = useState<CarListBand>();
	const [deletingEntities, setDeletingEntities] = useState<CarListBand[]>([]);
	const [selectedEntities, setSelectedEntities] = useState<CarListBand[]>([]);
	const [addModalOpen, setAddModalOpen] = useState(false);
	const { data: allEntities = [], trigger } = useService(() => carListBandService.getAll());
	const entities = searchText ? searchResult : allEntities;

	const handleAddModalClose = () => setAddModalOpen(false);
	const onAdd = (s: Partial<CarListBand>) =>
		RxUtils.promisify(
			carListBandService.create(s),
			() => (trigger(), notificationService.sendSuccess(t('car-list-bands.add-success-message')))
		);
	const onEdit = (s: Partial<CarListBand>) =>
		s.id &&
		RxUtils.promisify(
			carListBandService.update(s.id, s),
			() => (trigger(), notificationService.sendSuccess(t('car-list-bands.update-success-message')))
		);
	const onDelete = (s: CarListBand) =>
		RxUtils.promisify(
			carListBandService.delete(s),
			() => (trigger(), notificationService.sendSuccess(t('car-list-bands.delete-success-message')))
		);
	const onDeleteMultiple = (s: CarListBand[]) =>
		RxUtils.promisify(
			carListBandService.deleteMultiple(s),
			() => (trigger(), notificationService.sendSuccess(t('car-list-bands.delete-multiple-success-message')))
		);

	useEffect(() => {
		if (searchText) {
			RxUtils.promisify(carListBandService.search(searchText), setSearchResult);
		} else {
			setSearchResult([]);
		}
	}, [searchText]);

	return (
		<>
			{Array.isArray(allEntities) && allEntities.length ? (
				<Stack direction="column" spacing={3} sx={{ height: 1.0, maxHeight: 1.0, paddingY: 5 }}>
					<PortalPageTitle
						paddingX={paddingX}
						title={<Tr.Portal path="car-list-bands.title" />}
						subtitle={<Tr.Portal path="car-list-bands.subtitle" />}
						displaySearchbox
						searchboxProps={{
							testId: 'car-list-band-search-box',
							placeholder: t('car-list-bands.search-placeholder'),
							searchText,
							onSearchTextChange: setSearchText
						}}
					/>
					<CarListBandList
						showResultCount={!!searchText}
						paddingX={paddingX}
						entities={entities}
						selected={selectedEntities}
						onSelectChange={setSelectedEntities}
						onEdit={setEditingEntity}
						onDelete={(entity) => setDeletingEntities([entity])}
					/>
					{user?.hasPermission(Permission.CAR_LIST_BAND_WRITE) && (
						<Stack direction="row" spacing={3} justifyContent="center" sx={{ paddingX }}>
							{selectedEntities.length ? (
								<>
									<Button
										data-testid="delete-car-list-band-modal-open-button"
										variant="contained"
										color="secondary"
										sx={{ textTransform: 'uppercase' }}
										onClick={() => setDeletingEntities(selectedEntities)}
									>
										<Tr.Portal path="delete" />
									</Button>
									<Button
										data-testid="cancel-car-list-band-selection-button"
										variant="outlined"
										color="secondary"
										sx={{ textTransform: 'uppercase' }}
										onClick={() => setSelectedEntities([])}
									>
										<Tr.Portal path="cancel" />
									</Button>
								</>
							) : (
								<Button
									data-testid="add-car-list-band-modal-open-button"
									variant="contained"
									sx={{ textTransform: 'uppercase' }}
									onClick={() => setAddModalOpen(true)}
								>
									<Tr.Portal path="car-list-bands.add-car-list-band" />
								</Button>
							)}
						</Stack>
					)}
				</Stack>
			) : (
				<EmptyEntityPageLayout
					illustration={<Illustration />}
					title={<Tr.Portal path="car-list-bands.no-car-list-band-added-title" />}
					addButtonText={<Tr.Portal path="car-list-bands.add-car-list-band" />}
					onAddButtonClick={() => setAddModalOpen(true)}
					addButtonProps={{ 'data-testid': 'add-car-list-band-modal-open-button' }}
				/>
			)}

			<CarListBandAddUpdateDialog
				open={addModalOpen}
				onClose={handleAddModalClose}
				onPrimaryButtonClick={(s) => {
					handleAddModalClose();
					onAdd?.(s);
				}}
			/>
			<CarListBandAddUpdateDialog
				open={!!editingEntity}
				entity={editingEntity}
				onClose={() => setEditingEntity(undefined)}
				onPrimaryButtonClick={(s) => {
					setEditingEntity(undefined);
					onEdit?.(s);
				}}
			/>
			<EntityDeleteConfirmationDialog
				open={!!deletingEntities?.length}
				content={<Tr.Portal path="car-list-bands.delete-confirmation-message" />}
				buttonProps={{ 'data-testid': 'car-list-band-delete-confirmation-button' }}
				onClose={() => setDeletingEntities([])}
				onAccept={() => {
					deletingEntities.length > 1 ? onDeleteMultiple?.(deletingEntities) : onDelete?.(deletingEntities[0]);
					setDeletingEntities([]);
					setSelectedEntities([]);
				}}
			>
				{deletingEntities.map((e) => `"${e.label}"`).join(', ')}
			</EntityDeleteConfirmationDialog>
		</>
	);
};
